/**
 * Primary brand & SEO messaging - home salon / salon-at-home positioning (Delhi NCR).
 * Single source of truth for meta + on-page alignment.
 */

import { SITE_NAME } from "./config";

/** Core service categories for SEO & on-page copy (salon menu at home). */
export const SALON_SERVICES_SUMMARY =
  "facials, waxing, manicure & pedicure, hair spa, body spa, and more full salon services";

export const SEO_PRIMARY_TOPIC =
  "home salon and salon-at-home beauty services in Delhi NCR";

export const META_ROOT_TITLE_DEFAULT = `${SITE_NAME} - Home Salon & Salon at Home | Delhi NCR`;

export const META_ROOT_DESCRIPTION = `${SITE_NAME} is a leading home salon service provider: book salon-at-home appointments for ${SALON_SERVICES_SUMMARY}. Certified stylists and therapists, transparent pricing, same-day slots across Delhi NCR.`;

export const META_HOME_TITLE = `Home Salon & Salon at Home - Book Beauty Experts`;

export const META_HOME_DESCRIPTION = `Book ${SEO_PRIMARY_TOPIC} with ${SITE_NAME} - ${SALON_SERVICES_SUMMARY}. Vetted professionals, clear pricing, and easy scheduling.`;

export const META_HOME_OG_TITLE = `Home Salon & Salon at Home in Delhi NCR | ${SITE_NAME}`;

export const META_KEYWORDS = [
  "home salon",
  "salon at home",
  "facial at home",
  "waxing at home",
  "manicure at home",
  "pedicure at home",
  "hair spa at home",
  "body spa at home",
  "salon at home Delhi NCR",
  "home salon service provider",
  "beauty services at home",
  "book beautician at home",
  "at home salon Noida",
  "at home salon Gurgaon",
  SITE_NAME,
];

export function metaTitleHub(cityLabel: string): string {
  return `Home Salon & Salon at Home in ${cityLabel}`;
}

export function metaDescriptionHub(cityLabel: string): string {
  return `${SITE_NAME} brings salon-at-home services to ${cityLabel}: ${SALON_SERVICES_SUMMARY}. Book trusted professionals with transparent pricing.`;
}

export function metaTitleCategory(cityLabel: string, categoryLabel: string): string {
  return `${categoryLabel} at Home in ${cityLabel} - Salon Services`;
}

export function metaDescriptionCategory(
  cityLabel: string,
  categoryLabel: string
): string {
  return `Book ${categoryLabel.toLowerCase()} and other salon services (${SALON_SERVICES_SUMMARY}) at home in ${cityLabel}. ${SITE_NAME} offers salon-at-home appointments with certified professionals in Delhi NCR.`;
}

export function metaTitleSubcategory(
  cityLabel: string,
  categoryLabel: string,
  subcategoryLabel: string
): string {
  return `${subcategoryLabel} - ${categoryLabel} at Home (${cityLabel})`;
}

export function metaDescriptionSubcategory(
  cityLabel: string,
  categoryLabel: string,
  subcategoryLabel: string
): string {
  return `Book ${subcategoryLabel.toLowerCase()} (${categoryLabel.toLowerCase()}) at home in ${cityLabel}. Full salon menu including ${SALON_SERVICES_SUMMARY} with ${SITE_NAME} - pricing, slots & hygiene you can trust.`;
}

/**
 * Visible home hero - short, scannable (meta titles/descriptions stay keyword-rich separately).
 */
export const HERO_EYEBROW = (hubLabel: string) =>
  `Salon at home · ${hubLabel}`;

/** No hub in the H1 - long area names (e.g. “Sector 63 Noida”) read poorly; location stays on the eyebrow. */
export const HERO_HEADLINE = "Beauty & spa, delivered at home";

export const HERO_SUBHEAD = `From facials and waxing to manicures, hair spa, and body care - we bring the full salon to you. Trusted professionals, clear prices, and visits across Delhi NCR.`;

export const HERO_PILLS = [
  "Facials & waxing",
  "Hair spa & body spa",
  "Mani-pedi at home",
] as const;

/** Right-hand highlight card (home hero) */
export const HOME_HERO_CARD_KICKER = "Why SalonTym";
export const HOME_HERO_CARD_TITLE =
  "Spa-day treatments - without stepping out the door.";

/** Small promo tiles under the highlight card */
export const HOME_PERK_KICKER = "New here";
export const HOME_PERK_TITLE = "Try us with complimentary services.";
export const HOME_PROMISE_KICKER = "Our promise";
export const HOME_PROMISE_TITLE = "Fair pricing, easy booking, premium care.";

export const FOOTER_ABOUT_BLURB = `${SITE_NAME} is a home salon service provider: ${SALON_SERVICES_SUMMARY} at your doorstep across Delhi NCR - trusted professionals, transparent pricing, and easy booking.`;

export const CATEGORY_SUBTITLE =
  "Explore facials, waxing, spa, mani-pedi, and more - then book for your area in a few taps.";
